import {
    Material,
    Color,
    Primitive,
    GeometryInstance,
    PolylineGeometry,
    VertexFormat,
    PolylineMaterialAppearance,
    PolylineColorAppearance,
    Cartesian3,
    ColorGeometryInstanceAttribute,
} from 'cesium';
import parabola from '../_util/parabola';
import transformRgb from '../_util/transformRgb';
import CircleRippleMaterialProperty from '../map/material/CircleRippleMaterialProperty';

const FlowingLineMaterialGLSL =
    'float SPEED_STEP = 0.01; \n' +
    'vec4 drawLight(float xPos, vec2 st, float headOffset, float tailOffset, float widthOffset){ \n' +
    'float lineLength = smoothstep(xPos + headOffset, xPos, st.x) - smoothstep(xPos, xPos - tailOffset, st.x); \n' +
    'float lineWidth = smoothstep(widthOffset, 0.5, st.y) - smoothstep(0.5, 1.0 - widthOffset, st.y); \n' +
    'return vec4(lineLength * lineWidth); \n' +
    '}\n' +
    'czm_material czm_getMaterial(czm_materialInput materialInput) \n' +
    '{ \n' +
    'czm_material m = czm_getDefaultMaterial(materialInput);\n' +
    'float sinTime = sin(czm_frameNumber * SPEED_STEP * speed); \n' +
    'vec4 v4_core;\n' +
    'vec4 v4_color;\n' +
    'float xPos = 0.0; \n' +
    'if (sinTime < 0.0){ \n' +
    'xPos = cos(czm_frameNumber * SPEED_STEP * speed)+ 1.0 - tailSize; \n' +
    '}else{ \n' +
    'xPos = -cos(czm_frameNumber * SPEED_STEP * speed)+ 1.0 - tailSize; \n' +
    '} \n' +
    'v4_color = drawLight(xPos, materialInput.st, headSize, tailSize, widthOffset);\n' +
    'v4_core = drawLight(xPos, materialInput.st, coreSize, coreSize*2.0, widthOffset*2.0);\n' +
    'm.diffuse = color.xyz + v4_core.xyz*v4_core.w*0.8; \n' +
    'm.alpha = pow(v4_color.w, 3.0); \n' +
    'return m; \n' +
    '} \n';

class FlightLineLayer {
    constructor(options) {
        const { show = true } = options;
        this.map = options.map;

        this.data = options.data;

        this.color = options.color;

        this.endEffect = options.endEffect;

        this.entities = [];

        this.primitives = [];

        this.show = show;

        this.flyWidth = options.flyWidth;

        this.heightProportion = options.heightProportion;

        this.lineWidth = options.lineWidth;

        this.uniforms = options.uniforms || {};

        if (this.data?.length) {
            this.loadData(this.data);
        }
    }

    set show(value) {
        if (this.primitives?.length) {
            this.primitives.map(primitive => {
                primitive.show = value;
            });
        }

        if (this.entities?.length) {
            this.entities.map(item => {
                item.show = value;
            });
        }
    }

    loadData(data) {
        this.data = data;
        const map = this.map;
        const show = this.show;

        this.destroy();

        const colors = {};
        const line_instances = [];
        this.entities = [];
        this.primitives = [];

        data.map(item => {
            // 飞行线颜色
            const flyColor = item.color || this.color || '#05FFD5';

            // 轨迹颜色
            const lineColor = item.lineColor || this.lineColor || flyColor;

            // 轨迹颜色转换
            const line_color = Color.fromCssColorString(
                transformRgb(lineColor, item.lineCpacity || this.lineCpacity || 0.4),
            );

            // 飞行线颜色转换
            const color = Color.fromCssColorString(
                transformRgb(flyColor, item.opacity || this.opacity || 1),
            );

            // 缓存颜色
            const color_str = color.toString();
            colors[color_str] = colors[color_str] || {
                color,
                point_instances: [],
            };
            const distance = Cartesian3.distance(
                Cartesian3.fromDegrees(...item.start),
                Cartesian3.fromDegrees(...item.end),
            );

            const heightProportion = item.heightProportion || this.heightProportion || 0.23; // 自定义最高点和总距离的比值(即图中H比上AB的值)，值越大则抛物线越弯曲
            const height = item?.hasOwnProperty?.('height')
                ? item.height
                : distance * heightProportion;
            let positions = parabola({ startPosition: item.start, endPosition: item.end, height });

            line_instances.push(
                new GeometryInstance({
                    geometry: new PolylineGeometry({
                        positions: positions,
                        width: item.lineWidth || this.lineWidth || 2.0,
                        vertexFormat: PolylineColorAppearance.VERTEX_FORMAT,
                    }),
                    attributes: {
                        color: new ColorGeometryInstanceAttribute.fromColor(line_color),
                    },
                }),
            );

            colors[color_str].point_instances.push(
                new GeometryInstance({
                    geometry: new PolylineGeometry({
                        positions: positions,
                        width: item.flyWidth || this.flyWidth || 20.0,
                        vertexFormat: VertexFormat.ALL,
                    }),
                }),
            );

            if (this.endEffect?.show) {
                this.entities.push(
                    map.entities.add({
                        position: Cartesian3.fromDegrees(...item.end),
                        ellipse: {
                            semiMinorAxis: this.endEffect.radius || 500.0,
                            semiMajorAxis: this.endEffect.radius || 500.0,
                            material: new CircleRippleMaterialProperty({
                                color: Color.fromCssColorString(
                                    transformRgb(flyColor || '#f7ff01', 0.7),
                                ),
                                speed: 12.0,
                                count: 3,
                                gradient: 0.2,
                            }),
                        },
                        show,
                    }),
                );
            }
        });

        for (let color_str in colors) {
            const { color, point_instances } = colors[color_str];

            const point = new Primitive({
                geometryInstances: point_instances,
                show,
                appearance: new PolylineMaterialAppearance({
                    material: new Material({
                        fabric: {
                            type: 'FlowingLineMaterial',
                            uniforms: {
                                color: color, // light color
                                speed: 1.5, // flowing speed, speed > 0.0
                                headSize: 0.05, // 0.0 < headSize < 1.0
                                tailSize: 0.5, // 0.0 < tailSize < 1.0
                                widthOffset: 0.1, // 0.0 < widthOffset < 1.0
                                coreSize: 0.05, // 0.0 < coreSize < 1.0
                                ...this.uniforms,
                            },
                            source: FlowingLineMaterialGLSL,
                        },
                    }),
                }),
            });
            map.scene.primitives.add(point);
            this.primitives.push(point);
        }

        const line = new Primitive({
            geometryInstances: line_instances,
            show,
            appearance: new PolylineColorAppearance(),
        });
        map.scene.primitives.add(line);
        this.primitives.push(line);
    }

    destroy() {
        const map = this.map;

        if (this.primitives?.length) {
            this.primitives.map(primitive => {
                map.scene.primitives.remove(primitive);
            });
            this.primitives = null;
        }

        if (this.entities?.length) {
            this.entities.map(item => {
                map.entities.remove(item);
            });
            this.entities = null;
        }
    }
}

export default FlightLineLayer;
